import React from 'react';
import toast from 'react-hot-toast';
import {Alert, Space} from 'antd';
import ls from 'localstorage-slim';
import PrimaryTextButton from '../../components/buttons/PrimaryTextButton';
import IconWrapper from '../../components/IconWrapper';
import {
  expenseCategoryTagConstants,
  expensePaymentMethodConstants,
  expenseReviewStatusConstants,
  expenseTypesConstants,
  mccCategoriesConstants,
  localStorageKeysConstants,
} from '../../constants';
import {
  Invoice2Icon,
  InvoiceNoReceiptIcon,
  InvoiceRequestedIcon,
  FileInvoiceIcon,
  Unlock2Icon,
  UserIcon,
  WireIcon,
  WithLessCreditCardIcon,
  WithLessOutsideIcon,
} from '../../icons';
import {
  AdvertisingServicesIcon,
  AirlinesIcon,
  AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon,
  BooksPeriodicalsAndNewspapersIcon,
  BusinessServicesNotElsewhereClassifiedIcon,
  CommercialArtGraphicsPhotographyIcon,
  ComputerNetworkInformationServicesIcon,
  ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon,
  ComputerSoftwareStoresIcon,
  ConsultingManagementAndPublicRelationsServicesIcon,
  CourierServiceAirAndGroundIcon,
  DepartmentStoresIcon,
  DigitalGoodsIcon,
  DirectMarketingCombinationCatalogAndRetailMerchantIcon,
  ElectronicsSalesIcon,
  EmploymentAgenciesAndTemporaryHelpServicesIcon,
  EquipmentFurnitureAndHomeFurnishingsStoresIcon,
  FinesIcon,
  FloristsIcon,
  GroceryStoresSupermarketsIcon,
  HotelsIcon,
  MiscellaneousAndSpecialtyRetailStoresIcon,
  MiscellaneousFoodStoresIcon,
  MiscellaneousGeneralMerchandiseStoresIcon,
  MiscellaneousPublishingAndPrintingIcon,
  OtherIcon,
  PassengerRailwaysIcon,
  PhotographicStudiosIcon,
  ProfessionalServicesIcon,
  RealEstateAgentsAndManagersRentalsIcon,
  RecreationServicesIcon,
  RestaurantsIcon,
  SchoolsAndEducationalServicesIcon,
  SoftwareIcon,
  SouvenirShopsIcon,
  SubscriptionMerchantsIcon,
  TaxicabsIcon,
  TaxIcon,
  TelecommunicationEquipmentIcon,
  TelecommunicationServicesIcon,
  TransportationSuburbanIcon,
  TravelAgenciesAndTourOperatorsIcon,
  VarietyStoresIcon
} from '../../icons/mccCategories';
import {alertActions} from '../../state/actions/alert.actions';
import ObPaymentMethodCell from '../../components/ObPaymentMethodCell';
import {amountHelpers} from './amount.helpers';
import {dateHelpers} from './date.helpers';
import {objectHelpers} from './object.helpers';
import {transactionsHelpers} from './transactions.helpers';
import {vendorsHelpers} from './vendors.helpers';
import routes from '../../routes/routes.json';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../pages/ExpensesPage/expensesPage.constants';
import {reviewHelpers} from './review.helpers';
import {textHelpers} from './text.helpers';
import {cardsHelpers} from './cards.helpers';
import {getReviewStatusDisplayKey, PARTIALLY_REJECTED_DISPLAY_KEY} from './expenseStatusDisplay.helpers';

export const expenseReviewStatuses = {
  [expenseReviewStatusConstants.APPROVED]: {
    backgroundColor: '#52C41A33',
    color: '#52C41A',
    key: 'expenseReviewStatuses.approved',
  },
  [expenseReviewStatusConstants.PARTIALLY_APPROVED]: {
    backgroundColor: '#52C41A33',
    color: '#52C41A',
    key: 'expenseReviewStatuses.partially_approved',
  },
  [PARTIALLY_REJECTED_DISPLAY_KEY]: {
    backgroundColor: '#FEE4E2',
    color: '#FF4D4F',
    key: 'expenseReviewStatuses.partially_rejected',
  },
  [expenseReviewStatusConstants.DRAFT]: {
    backgroundColor: '#C6E7F2',
    color: '#00465D',
    key: 'expenseReviewStatuses.draft',
  },
  [expenseReviewStatusConstants.IN_REVIEW]: {
    key: 'expenseReviewStatuses.in_review',
    color: '#7A2E0E',
    backgroundColor: '#FEF0C7',
  },
  [expenseReviewStatusConstants.RECLAIMED]: {
    backgroundColor: '#D92D20',
    color: '#FEE4E2',
    key: 'expenseReviewStatuses.reclaimed',
  },
  [expenseReviewStatusConstants.REIMBURSED]: {
    backgroundColor: '#245F07',
    color: '#D6FFCE',
    key: 'expenseReviewStatuses.reimbursed',
  },
  [expenseReviewStatusConstants.REJECTED]: {
    backgroundColor: '#FEE4E2',
    color: '#FF4D4F',
    key: 'expenseReviewStatuses.rejected',
  },
};

// Fixed display order for review status filters/selectors, independent of the
// expenseReviewStatuses object's key order.
const EXPENSE_REVIEW_STATUS_ORDER = [
  expenseReviewStatusConstants.DRAFT,
  expenseReviewStatusConstants.IN_REVIEW,
  expenseReviewStatusConstants.APPROVED,
  expenseReviewStatusConstants.PARTIALLY_APPROVED,
  expenseReviewStatusConstants.REIMBURSED,
  expenseReviewStatusConstants.REJECTED,
  expenseReviewStatusConstants.RECLAIMED,
];

const EXPENSE_TYPE_ICON_MAP = {
  [expenseTypesConstants.INVOICE]: FileInvoiceIcon,
  [expenseTypesConstants.RECEIPT]: Invoice2Icon,
  [expenseTypesConstants.NO_RECEIPT]: InvoiceNoReceiptIcon,
};

const EXPENSE_TYPE_LABEL_MAP = {
  [expenseTypesConstants.INVOICE]: 'main:expenseTypes.invoice',
  [expenseTypesConstants.RECEIPT]: 'main:expenseTypes.receipt',
  [expenseTypesConstants.NO_RECEIPT]: 'main:expenseTypes.noReceipt',
};


/**
 * Collection of utility helpers for expense data formatting, display, and business logic.
 * @namespace expensesHelpers
 * @example
 * import { expensesHelpers } from './expenses.helpers';
 */

// "<last-4 IBAN> <bank name>", trimmed and cut to 20 chars. Single source of truth
// shared by the action that first builds an option and the reducer that re-syncs
// its bank name against account renames, so both always produce identical labels.
export const getObPaymentMethodOptionLabel = (iban, bankName) =>
  textHelpers.cutString(`${cardsHelpers.getLast4Digits(iban)} ${bankName ?? ''}`.trim(), 20);

export const getGroupExpenses = (rows, groupRow) => {
  const idx = rows.indexOf(groupRow);
  const result = [];
  for (let i = idx + 1; i < rows.length; i++) {
    if (rows[i].isMonthGrouping) break;
    result.push(rows[i]);
  }
  return result;
};

export const expensesHelpers = {
  /**
   * Returns a formatted date string for an expense, supporting both single dates and date ranges.
   * @param {Object} expense - Expense object with expense_date or created_at.
   * @param {string} [format='DD MMM YYYY'] - Moment.js-compatible date format string.
   * @returns {string|null} Formatted date or date range string, or null when no date is available.
   * @example
   * expensesHelpers.getFormattedExpenseDate(expense); // '15 Jan 2024'
   */
  getFormattedExpenseDate: (expense, format = 'DD MMM YYYY') => {
    if (!expense || typeof expense !== 'object') return null;

    const rawExpenseDate = expense?.expense_date;

    let startRaw = expense?.expense_date;
    let endRaw = expense?.expense_end_date;

    // support range format "YYYY-MM-DD#YYYY-MM-DD" for old expenses
    if (rawExpenseDate?.includes('#')) {
      [startRaw, endRaw] = rawExpenseDate.split('#').map(s => s.trim());
    }

    if (startRaw && typeof startRaw === 'string') {
      if (!startRaw) return null;
      const startFormatted = dateHelpers.getDateFromISO(startRaw, format);
      if (endRaw) {
        const endFormatted = dateHelpers.getDateFromISO(endRaw, format);
        if (startFormatted === endFormatted) return startFormatted;

        return `${startFormatted} - ${endFormatted}`;
      }
      return startFormatted;
    }

    const start = rawExpenseDate || expense?.created_at;
    if (!start) return null;

    return dateHelpers.getDateFromISO(start, format);
  },
  /**
   * Sorts expenses by created_at and groups them by month, inserting a group header row for each month.
   * The header rows carry no figure: a month's total is the backend's, not a sum of the rows that happen
   * to be loaded, so it is applied separately by applyMonthlyTotals.
   * @param {Array<Object>} expenses - Array of expense objects with a created_at property.
   * @returns {Array<Object>} Interleaved array of month group rows and individual expense objects.
   * @example
   * expensesHelpers.getGroupedExpenses(expenseList);
   */
  getGroupedExpenses: (expenses) => {
    const groupedExpenses = [];
    let lastAdded;

    // sort expenses by created_at
    expenses = expenses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Add grouping rows and transactions
    expenses.forEach(exp => {
      const date = exp.created_at.slice(0, 7);
      if (lastAdded !== date) {
        groupedExpenses.push({
          id: date,
          isMonthGrouping: true,
          created_at: date,
        });
        lastAdded = date;
      }
      groupedExpenses.push(exp);
    });

    return transactionsHelpers.additionalTransactionsGrouping(groupedExpenses, 'isMonthGrouping');
  },
  /**
   * Returns the key the monthly-spending endpoint uses for a month: "MM-YYYY".
   * Taken from the ISO prefix of created_at (no timezone conversion), like the month header rows are.
   * @param {string} createdAt - ISO timestamp, or the "YYYY-MM" prefix of one.
   * @returns {string} Month key.
   * @example
   * expensesHelpers.getMonthlySpendingKey('2026-10-08T10:00:00Z'); // '10-2026'
   */
  getMonthlySpendingKey: (createdAt) => `${createdAt.slice(5, 7)}-${createdAt.slice(0, 4)}`,
  /**
   * Builds the monthly-spending query from the All tab's filter query: the same filters as the list,
   * minus paging and ordering, plus the three params the endpoint requires. With a month and year
   * selected the range is that month and date_field follows the filter; otherwise it spans the first to
   * the last month of the loaded expenses (by creation date, which is what the month headers group by),
   * whole months at both ends, so every month header on screen has its full total.
   * @param {Object} query - The tab's raw filter query (month, year, date_field and list filters).
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.expenses] - Loaded rows, used for the range when no period is selected.
   * @returns {Object|null} Query for expenseActions.getExpensesMonthlySpending, or null when there is no
   *   period and nothing is loaded to take a range from.
   * @example
   * expensesHelpers.getMonthlySpendingQuery({month: 5, year: 2024, date_field: 'expense_date'}, {});
   * // {date_field: 'expense_date', start_date: '2024-05-01', end_date: '2024-05-31', expense_date_start: '2024-05-01', expense_date_end: '2024-05-31'}
   */
  getMonthlySpendingQuery: (query, {expenses = []} = {}) => {
    const {month, year, date_field, cursor, per_page, order_by, ...filters} = query;
    const getBounds = (monthKey) => expensesHelpers.getExpenseListFilterValues({
      month: Number(monthKey.slice(5, 7)),
      year: Number(monthKey.slice(0, 4)),
      date_field: 'created_at',
    });

    const selectedPeriod = expensesHelpers.getExpenseListFilterValues({month, year, date_field: 'created_at'});
    if (selectedPeriod) {
      const isExpenseDate = date_field === 'expense_date';
      return {
        ...filters,
        ...selectedPeriod,
        ...(isExpenseDate ? {expense_date_start: selectedPeriod.start_date, expense_date_end: selectedPeriod.end_date} : {}),
        date_field: isExpenseDate ? 'expense_date' : 'created_at',
      };
    }

    const months = expenses
      .filter(expense => !expense.isMonthGrouping)
      .map(expense => expense.created_at.slice(0, 7))
      .sort();
    if (!months.length) return null;

    return {
      ...filters,
      start_date: getBounds(months[0]).start_date,
      end_date: getBounds(months[months.length - 1]).end_date,
      date_field: 'created_at',
    };
  },
  /**
   * Fills the month header rows with the backend's monthly totals.
   * While the totals load the header gets totalsLoading; when they are missing or failed it gets
   * totalsUnavailable, so the cell renders nothing instead of a wrong or zero figure.
   * @param {Array<Object>} rows - Rows from getGroupedExpenses.
   * @param {Object} totals - {status: 'idle'|'loading'|'ready'|'error', refreshing: boolean, byMonth: {[MM-YYYY]: number}}.
   *   While refreshing, a month with no figure yet (newly loaded) shows the skeleton rather than nothing.
   * @returns {Array<Object>} Rows with month headers carrying price, totalsLoading or totalsUnavailable.
   * @example
   * expensesHelpers.applyMonthlyTotals(rows, {status: 'ready', byMonth: {'10-2026': 1200}});
   */
  applyMonthlyTotals: (rows, {status, byMonth, refreshing}) => rows.map(row => {
    if (!row.isMonthGrouping) return row;
    if (status === 'loading') return {...row, totalsLoading: true};
    const total = status === 'ready' ? byMonth[expensesHelpers.getMonthlySpendingKey(row.created_at)] : null;
    if (total === null || total === undefined) {
      return refreshing ? {...row, totalsLoading: true} : {...row, totalsUnavailable: true};
    }
    return {...row, price: total};
  }),
  /**
   * Returns a styled label element for the given expense review status value.
   * @param {Object} params
   * @param {string} params.value - Review status constant (e.g. 'approved', 'in_review').
   * @param {Function} params.t - i18n translation function.
   * @param {React.ReactNode} [params.suffix] - Inline node rendered after the label text (e.g. an edit icon).
   * @param {boolean} [params.isReclaimContext] - True when shown from the To Reclaim tab: `partially_approved`
   * is then displayed as the red "Partially rejected". Other statuses are unaffected.
   * @returns {React.ReactElement|null} Styled label element or null when the status is unrecognised.
   * @example
   * expensesHelpers.getReviewStatusLabel({value: 'approved', t});
   * expensesHelpers.getReviewStatusLabel({value: 'partially_approved', t, isReclaimContext: true}); // "Partially rejected"
   */
  getReviewStatusLabel: ({value, t, suffix, isReclaimContext = false} = {}) => {
    return reviewHelpers.getStatusLabel({value: getReviewStatusDisplayKey(value, isReclaimContext), statuses: expenseReviewStatuses, t, suffix});
  },
  /**
   * Returns review status filter options in the fixed display order: Draft, In Review,
   * Approved, Reimbursed, Rejected, Reclaimed.
   * @param {Function} t - i18n translation function.
   * @returns {Array<{label: React.ReactElement, value: string}>} Ordered review status options.
   * @example
   * expensesHelpers.getReviewStatusOptions(t);
   */
  getReviewStatusOptions: (t) => {
    return EXPENSE_REVIEW_STATUS_ORDER.map(status => ({
      label: expensesHelpers.getReviewStatusLabel({value: status, t}),
      value: status,
    }));
  },
  /**
   * Returns the MCC category icon component for the given category constant value.
   * @param {string} value - MCC category constant string from mccCategoriesConstants.
   * @returns {React.ReactElement} Matching category icon, or OtherIcon if no match is found.
   * @example
   * expensesHelpers.getExpenseCategoryIcon(mccCategoriesConstants.AIRLINES);
   */
  getExpenseCategoryIcon: (value) => {
    return {
      [mccCategoriesConstants.ADVERTISING_SERVICES]: <AdvertisingServicesIcon />,
      [mccCategoriesConstants.AIRLINES]: <AirlinesIcon />,
      [mccCategoriesConstants.ATHLETIC_FIELDS_COMMERCIAL_SPORTS_PROFESSIONAL_SPORTS_CLUBS_SPORTS_PROMOTERS]: <AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon />,
      [mccCategoriesConstants.BOOKS_PERIODICALS_AND_NEWSPAPERS]: <BooksPeriodicalsAndNewspapersIcon />,
      [mccCategoriesConstants.BUSINESS_SERVICES_NOT_ELSEWHERE_CLASSIFIED]: <BusinessServicesNotElsewhereClassifiedIcon />,
      [mccCategoriesConstants.COMMERCIAL_ART_GRAPHICS_PHOTOGRAPHY]: <CommercialArtGraphicsPhotographyIcon />,
      [mccCategoriesConstants.COMPUTER_NETWORK_INFORMATION_SERVICES]: <ComputerNetworkInformationServicesIcon />,
      [mccCategoriesConstants.COMPUTER_PROGRAMMING_DATA_PROCESSING_AND_INTEGRATED_SYSTEM_DESIGN_SERVICES]: <ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon />,
      [mccCategoriesConstants.COMPUTER_SOFTWARE_STORES]: <ComputerSoftwareStoresIcon />,
      [mccCategoriesConstants.CONSULTING_MANAGEMENT_AND_PUBLIC_RELATIONS_SERVICES]: <ConsultingManagementAndPublicRelationsServicesIcon />,
      [mccCategoriesConstants.COURIER_SERVICES_AIR_AND_GROUND]: <CourierServiceAirAndGroundIcon />,
      [mccCategoriesConstants.DEPARTMENT_STORES]: <DepartmentStoresIcon />,
      [mccCategoriesConstants.DIGITAL_GOODS]: <DigitalGoodsIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_COMBINATION_CATALOG_AND_RETAIL_MERCHANT]: <DirectMarketingCombinationCatalogAndRetailMerchantIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_CONTINUITY_SUBSCRIPTION_MERCHANTS]: <SubscriptionMerchantsIcon />,
      [mccCategoriesConstants.ELECTRONICS_SALES]: <ElectronicsSalesIcon />,
      [mccCategoriesConstants.EMPLOYMENT_AGENCIES_AND_TEMPORARY_HELP_SERVICES]: <EmploymentAgenciesAndTemporaryHelpServicesIcon />,
      [mccCategoriesConstants.EQUIPMENT_FURNITURE_AND_HOME_FURNISHINGS_STORES]: <EquipmentFurnitureAndHomeFurnishingsStoresIcon />,
      [mccCategoriesConstants.FINES]: <FinesIcon />,
      [mccCategoriesConstants.FLORISTS]: <FloristsIcon />,
      [mccCategoriesConstants.GROCERY_STORES_SUPERMARKETS]: <GroceryStoresSupermarketsIcon />,
      [mccCategoriesConstants.HOTELS]: <HotelsIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_AND_SPECIALTY_RETAIL_STORES]: <MiscellaneousAndSpecialtyRetailStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_FOOD_STORES]: <MiscellaneousFoodStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_GENERAL_MERCHANDISE_STORES]: <MiscellaneousGeneralMerchandiseStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_PUBLISHING_AND_PRINTING]: <MiscellaneousPublishingAndPrintingIcon />,
      [mccCategoriesConstants.PASSENGER_RAILWAYS]: <PassengerRailwaysIcon />,
      [mccCategoriesConstants.PHOTOGRAPHIC_STUDIOS]: <PhotographicStudiosIcon />,
      [mccCategoriesConstants.PROFESSIONAL_SERVICES]: <ProfessionalServicesIcon />,
      [mccCategoriesConstants.REAL_ESTATE_AGENTS_AND_MANAGERS_RENTALS]: <RealEstateAgentsAndManagersRentalsIcon />,
      [mccCategoriesConstants.RECREATION_SERVICES]: <RecreationServicesIcon />,
      [mccCategoriesConstants.RESTAURANTS]: <RestaurantsIcon />,
      [mccCategoriesConstants.SCHOOLS_AND_EDUCATIONAL_SERVICES]: <SchoolsAndEducationalServicesIcon />,
      [mccCategoriesConstants.SOFTWARE]: <SoftwareIcon />,
      [mccCategoriesConstants.SOUVENIR_SHOPS]: <SouvenirShopsIcon />,
      [mccCategoriesConstants.TAXICABS]: <TaxicabsIcon />,
      [mccCategoriesConstants.TAX_PAYMENTS]: <TaxIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_EQUIPMENT]: <TelecommunicationEquipmentIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_SERVICES]: <TelecommunicationServicesIcon />,
      [mccCategoriesConstants.TRANSPORTATION_SUBURBAN]: <TransportationSuburbanIcon />,
      [mccCategoriesConstants.TRAVEL_AGENCIES_AND_TOUR_OPERATORS]: <TravelAgenciesAndTourOperatorsIcon />,
      [mccCategoriesConstants.VARIETY_STORES]: <VarietyStoresIcon />,
    }[value] ?? <OtherIcon />;
  },
  /**
   * Returns the approval reviews for an expense, or an empty array when quick approval is available.
   * @param {Object} expense - Expense object containing review and approval data.
   * @param {string} employeeEmail - Email of the currently authenticated employee.
   * @returns {Array<Object>} Array of approval review objects, or empty array for quick approval.
   * @example
   * expensesHelpers.getExpenseApprovalReviews(expense, 'user@example.com');
   */
  getExpenseApprovalReviews: (expense, employeeEmail) => {
    const canQuickApprove = reviewHelpers.canQuickApprove({data: expense, employeeEmail});
    return canQuickApprove ? [] : reviewHelpers.getApprovals(expense);
  },
  /**
   * Builds the URL for the expense details view, including the selected expense ID.
   * @param {Object} expense - Expense object with id.
   * @returns {string|null} Full URL string with query params, or null when expense has no id.
   * @example
   * expensesHelpers.getExpenseDetailsUrl({ id: 'e1' });
   */
  getExpenseDetailsUrl: (expense) => {
    if (!expense?.id) return null;
    return `${routes.expensesList}?${EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID}=${expense.id}`;
  },

  /**
   * Returns the formatted price of an expense as a currency string, negating positive values.
   * @param {Object} expense - Expense object with a price property.
   * @returns {string|null} Formatted currency string, or null when expense is invalid.
   * @example
   * expensesHelpers.getExpensePrice({ price: 150, currency: 'EUR' }); // '-€150.00'
   */
  getExpensePrice: (expense) => {
    if (!expense || typeof expense !== 'object') return null;

    const price = expense?.price;
    const currencyCode = expense?.currency;
    return amountHelpers.getAmountWithCurrencyCode(price > 0 ? -price : price, {currencyCode});
  },

  /**
   * Returns true when the expense carries an approved amount, that is, it was partially approved.
   * @param {Object} expense - Expense object; approved_amount arrives as a string or null.
   * @returns {boolean} True if approved_amount is set.
   * @example
   * expensesHelpers.hasApprovedAmount({ approved_amount: '70.00' }); // true
   */
  hasApprovedAmount: (expense) => expense?.approved_amount !== null && expense?.approved_amount !== undefined && expense?.approved_amount !== '',

  /**
   * Returns the amount the To Reclaim and To Reimburse tabs show for an expense.
   * Without an approved amount this is the price. With one, it is the approved part for an employee-paid
   * expense (what the company reimburses) and the remainder for any other payment method (what it reclaims).
   * @param {Object} expense - Expense with price, optional approved_amount (a string), currency and payment_method.
   * @returns {React.ReactElement|string|null} Formatted amount, or null when expense is invalid.
   * @example
   * expensesHelpers.getExpenseTabPrice({ price: 100, approved_amount: '70', payment_method: 'card' }); // '-€30,00'
   */
  getExpenseTabPrice: (expense) => {
    if (!expense || typeof expense !== 'object') return null;
    if (!expensesHelpers.hasApprovedAmount(expense)) {
      return expensesHelpers.getExpensePrice(expense);
    }

    const price = Number(expense.price);
    const approved = Number(expense.approved_amount);
    const isPaidByEmployee = expense.payment_method === expensePaymentMethodConstants.PAID_BY_EMPLOYEE;
    const amount = isPaidByEmployee ? approved : price - approved;
    return amountHelpers.getAmountWithCurrencyCode(amount > 0 ? -amount : amount, {currencyCode: expense.currency});
  },

  /**
   * Returns a vendor name element (text or link) derived from the expense's vendor data.
   * @param {Object} expense - Expense object with vendor_local_id, vendor_local_name, and vendor.
   * @param {'text'|'link'} [variant='text'] - Display variant for the vendor name.
   * @param {Function} [onClick] - When provided, the vendor icon triggers this instead of navigating.
   * @returns {React.ReactElement|null} Vendor name element or null when expense is invalid.
   * @example
   * expensesHelpers.getExpenseVendorNameWithLink(expense, 'link');
   */
  getExpenseVendorNameWithLink: (expense, variant = 'text', onClick) => {
    if (!expense || typeof expense !== 'object') return null;
    return vendorsHelpers.getVendorNameWithLink({
      vendor: {
        vendor_id: expense?.vendor_local_id,
        name: expense?.vendor_local_name || expense?.vendor,
      },
      variant,
      onClick,
    });
  },

  /**
   * Returns a styled review status label for the given expense.
   * @param {Object} params
   * @param {Object} params.expense - Expense object with a review_status property.
   * @param {Function} params.t - i18n translation function.
   * @param {boolean} [params.isReclaimContext] - True on the To Reclaim tab; see `getReviewStatusLabel`.
   * @returns {React.ReactElement|null} Styled label element or null when status is unrecognised.
   * @example
   * expensesHelpers.getExpenseStatusLabel({expense, t});
   */
  getExpenseStatusLabel: ({expense, t, isReclaimContext = false} = {}) => {
    const reviewStatus = expense?.review_status;
    return expensesHelpers.getReviewStatusLabel({value: reviewStatus, t, isReclaimContext});
  },

  /**
   * Returns an icon element representing the expense's document type.
   * INVOICE → FileInvoiceIcon, RECEIPT → Invoice2Icon, NO_RECEIPT/null/undefined → InvoiceNoReceiptIcon.
   * A requested invoice (invoice_requested) → InvoiceRequestedIcon, whatever the type.
   * @param {Object|null} expense - Expense object with expense_type and invoice_requested properties.
   * @returns {React.ReactElement|null} Icon matching the expense type, or null when expense is falsy or type is unrecognised.
   * @example
   * expensesHelpers.getExpenseTypeIcon({ expense_type: 'invoice' });   // <FileInvoiceIcon />
   * expensesHelpers.getExpenseTypeIcon({ expense_type: 'receipt' });   // <Invoice2Icon />
   * expensesHelpers.getExpenseTypeIcon({ expense_type: null });        // <InvoiceNoReceiptIcon />
   */
  getExpenseTypeIcon: (expense) => {
    if (!expense) return null;
    if (expense.invoice_requested) return <InvoiceRequestedIcon className='expense-type-icon' />;
    const expenseType = expense?.expense_type;

    const Icon =
      EXPENSE_TYPE_ICON_MAP[expenseType] ??
      (expenseType == null ? InvoiceNoReceiptIcon : null);

    return Icon ? <Icon className={`expense-type-icon ${expenseType == null ? 'missing-invoice-icon' : ''}`} /> : null;
  },
  /**
   * Returns an object with the icon element and translated label for the expense's document type.
   * INVOICE → { icon: <FileInvoiceIcon />, label: 'Invoice' }
   * RECEIPT → { icon: <Invoice2Icon />, label: 'Receipt' }
   * NO_RECEIPT/null/undefined → { icon: <InvoiceNoReceiptIcon />, label: 'No Receipt' }
   * A requested invoice (invoice_requested) gets InvoiceRequestedIcon with the label of its type.
   * @param {Object|null} expense - Expense object with expense_type and invoice_requested properties.
   * @param {Function} t - i18n translation function.
   * @returns {{ icon: React.ReactElement|null, label: string|null }|null} Icon and label pair, or null when expense is falsy or type is unrecognised.
   * @example
   * expensesHelpers.getExpenseTypeIconWithLabel({ expense_type: 0 }, t); // { icon: <FileInvoiceIcon />, label: 'Invoice' }
   * expensesHelpers.getExpenseTypeIconWithLabel({ expense_type: null }, t); // { icon: <InvoiceNoReceiptIcon />, label: 'No Receipt' }
   */
  getExpenseTypeIconWithLabel: (expense, t) => {
    if (!expense) return null;
    const expenseType = expense?.expense_type;

    const Icon = EXPENSE_TYPE_ICON_MAP[expenseType] ?? (expenseType == null ? InvoiceNoReceiptIcon : null);
    const labelKey = EXPENSE_TYPE_LABEL_MAP[expenseType] ?? (expenseType == null ? 'main:expenseTypes.noReceipt' : null);

    if (expense.invoice_requested) {
      return {icon: <InvoiceRequestedIcon className='expense-type-icon' />, label: labelKey ? t?.(labelKey) : null};
    }

    return {
      icon: Icon ? <Icon className='expense-type-icon' /> : null,
      label: labelKey ? t?.(labelKey) : null,
    };
  },
  /**
   * Returns a Space element displaying the payment method icon and label for an expense.
   * @param {Object} expense - Expense object with payment_method and optional card data.
   * @param {Function} t - i18n translation function.
   * @returns {React.ReactElement|null} Payment method display element or null when method is unknown.
   * @example
   * expensesHelpers.getExpensePaymentMethod(expense, t);
   */
  getExpensePaymentMethod: (expense, t) => {
    if (!expense || typeof expense !== 'object') return null;
    const paymentMethod = expense?.payment_method;

    if (paymentMethod === expensePaymentMethodConstants.CARD) {
      const cardId = expense?.card_id;
      const userId = expense?.card?.owner?.id;

      const cardDetails = {
        card_id: cardId,
        card_name: expense?.card?.name,
        masked_pan: expense?.card?.last_4_digits,
        user_id: userId,
      }

      const value = transactionsHelpers.getTransactionCardNumberWithLink({
        transaction: cardDetails, cardSymbolsLength: 5, nameSymbolsLength: 9
      });
      return (
        <Space size={4} align='center' className='d-flex d-flex-child'>
          <IconWrapper>{<WithLessCreditCardIcon />}</IconWrapper>
          <span className={`d-inline-flex ${cardId && userId ? ' active-link' : ''} no-wrap`}>{value}</span>
        </Space>
      );
    }

    if (paymentMethod === expensePaymentMethodConstants.OPEN_BANKING && expense?.ob_payment_method) {
      return <ObPaymentMethodCell obPaymentMethod={expense.ob_payment_method} />;
    }

    const paymentMethodDetails = {
      [expensePaymentMethodConstants.PAID_BY_EMPLOYEE]: {
        icon: <UserIcon />,
        name: 'cash',
      },
      [expensePaymentMethodConstants.WIRE]: {
        icon: <WireIcon />,
        name: 'wire',
      },
      [expensePaymentMethodConstants.OPEN_BANKING]: {
        icon: <Unlock2Icon />,
        name: 'openBanking',
      },
    }[expense?.payment_method] || undefined;

    if (!paymentMethodDetails) return null;

    return (
      <Space size='small' className='d-flex d-flex-child'>
        <IconWrapper>{paymentMethodDetails.icon}</IconWrapper>
        <span className='no-wrap'>{t(`paymentMethods.${paymentMethodDetails.name}`)}</span>
      </Space>
    );
  },
  /**
   * Returns an icon element representing the expense's payment method, with an optional override for outside payments.
   * @param {Object} expense - Expense object with a payment_method property.
   * @param {boolean} [isOutside=false] - When true, returns the outside payment icon regardless of method.
   * @returns {React.ReactElement|null} Icon element for the payment method, or null when method is unknown.
   * @example
   * expensesHelpers.getExpensePaymentTypeIcon(expense); // returns method icon
   * expensesHelpers.getExpensePaymentTypeIcon(expense, true); // returns outside icon
   * expensesHelpers.getExpensePaymentTypeIcon({ payment_method: 'cash' }); // returns cash icon
  */
  getExpensePaymentTypeIcon: (expense, isOutside = false) => {
    if (isOutside) return <WithLessOutsideIcon />;
    return {
      [expensePaymentMethodConstants.PAID_BY_EMPLOYEE]: <UserIcon />,
      [expensePaymentMethodConstants.WIRE]: <WireIcon />,
      [expensePaymentMethodConstants.CARD]: <WithLessCreditCardIcon />,
      [expensePaymentMethodConstants.OPEN_BANKING]: <Unlock2Icon />,
    }[expense?.payment_method] || null;
  },
  /**
   * Returns true when the expense has a defined (non-null, non-undefined) expense_type.
   * @param {Object} expense - Expense object with an expense_type property.
   * @returns {boolean} True if expense_type is a valid non-empty value.
   * @example
   * expensesHelpers.isDefinedExpenseType({ expense_type: 'invoice' }); // true
   */
  isDefinedExpenseType: (expense) => objectHelpers.isValidValue(expense?.expense_type),
  /**
   * Returns true when the expense's review_status is IN_REVIEW.
   * @param {Object} expense - Expense object with a review_status property.
   * @returns {boolean} True if the expense is currently in the review workflow.
   * @example
   * expensesHelpers.isExpenseInReview({ review_status: 'in_review' }); // true
   */
  isExpenseInReview: (expense) => expense?.review_status === expenseReviewStatusConstants.IN_REVIEW,
  /**
   * Returns true when the expense's review_status is DRAFT.
   * @param {Object} expense - Expense object with a review_status property.
   * @returns {boolean} True if the expense is in draft state.
   * @example
   * expensesHelpers.isExpenseDraft({ review_status: 'draft' }); // true
   */
  isExpenseDraft: (expense) => expense?.review_status === expenseReviewStatusConstants.DRAFT,
  /**
   * Returns true when the expense's review_status is either IN_REVIEW or DRAFT.
   * @param {Object} expense - Expense object with a review_status property.
   * @returns {boolean} True if the expense is in a mutable (draft or in-review) state.
   * @example
   * expensesHelpers.isExpenseInReviewOrDraft({ review_status: 'draft' }); // true
   */
  isExpenseInReviewOrDraft: (expense) => {
    const reviewStatus = expense?.review_status;
    return [expenseReviewStatusConstants.IN_REVIEW, expenseReviewStatusConstants.DRAFT].includes(reviewStatus);
  },
  /**
   * Returns true when the current user is eligible to approve or reject the expense.
   * @param {Object} params - Parameters object.
   * @param {string} params.employeeEmail - Email of the currently authenticated employee.
   * @param {Object} params.expense - Expense object to evaluate.
   * @param {boolean} [params.isAdmin=false] - When true, admin override applies even without being in the approval list.
   * @returns {boolean} True if approve/reject actions are available for this user.
   * @example
   * expensesHelpers.isAvailableApproveActions({ employeeEmail: 'mgr@co.com', expense, isAdmin: false });
   */
  isAvailableApproveActions: ({employeeEmail, expense, isAdmin = false}) => {
    const isInReviewStatus = expensesHelpers.isExpenseInReview(expense);
    const isInApprovalList = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);

    return isInReviewStatus && !isAlreadyApproved && (isAdmin || isInApprovalList);
  },
  /**
   * Returns true when the employee is a pending approver of the expense — listed in the
   * approval array but has not approved it yet.
   * @param {Object} expense - Expense object containing an approval array.
   * @param {string} employeeEmail - Email of the employee to check.
   * @returns {boolean} True if the employee is in the approval list and has not yet approved.
   * @example
   * expensesHelpers.isPendingApprover(expense, 'mgr@co.com'); // true
   */
  isPendingApprover: (expense, employeeEmail) => {
    const isInApprovalList = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);
    return isInApprovalList && !isAlreadyApproved;
  },
  /**
   * Returns true when the expense has already been reviewed (APPROVED/REJECTED/RECLAIMED/REIMBURSED).
   * @param {Object} expense - Expense object with a review_status property.
   * @returns {boolean} True if the expense review is complete.
   * @example
   * expensesHelpers.isAlreadyReviewed({ review_status: 'approved' }); // true
   */
  isAlreadyReviewed: (expense) => {
    const reviewStatus = expense?.review_status;
    if (!reviewStatus) return false;
    return [expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.PARTIALLY_APPROVED,
      expenseReviewStatusConstants.REJECTED, expenseReviewStatusConstants.RECLAIMED,
      expenseReviewStatusConstants.REIMBURSED].includes(reviewStatus);
  },

  /**
   * Displays an error alert using the API response message or a translated fallback.
   * @param {Object} params - Parameters object.
   * @param {Object} params.response - Axios error response object.
   * @param {string} params.fallbackKey - Translation key suffix used when no API message is available.
   * @param {Function} params.t - i18n translation function.
   * @returns {void}
   * @example
   * expensesHelpers.handleExpenseError({ response, fallbackKey: 'submitFailed', t });
   */
  handleExpenseError: ({response, fallbackKey, t}) => {
    const message = typeof response === 'string' ? response : response?.data?.message || response?.response?.data?.message || t(`${`expenses:validation.${fallbackKey}`}`);
    alertActions.error(message);
  },

  /**
   * Finds and returns a related object from a data list using an ID stored on the open expense.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.detailsWindowProps - Window state object with open flag and expense data.
   * @param {Array<Object>} [params.data=[]] - Array of related objects to search through.
   * @param {string} params.key - Property key on the expense used to look up the related object's id.
   * @returns {Object|undefined} Matching related object, or undefined when not found or window is closed.
   * @example
   * expensesHelpers.getExpenseRelatedObject({ detailsWindowProps, data: vendors, key: 'vendor_id' });
   */
  getExpenseRelatedObject: ({detailsWindowProps, data = [], key} = {}) => {
    if (!detailsWindowProps.open) return ;
    let obj;
    const id = detailsWindowProps?.expense?.[key];
    if (detailsWindowProps.open && id) obj = data.find(s => s.id === id);
    return obj;
  },

  /**
   * Returns the expense's linked transaction id. Open banking expenses store it
   * in `saltedge_id`; all other payment methods use `card_transaction_id`.
   * @param {Object} expense - Expense object.
   * @returns {string|undefined} The linked transaction id, or undefined when absent.
   * @example
   * expensesHelpers.getExpenseTransactionId(expense);
   */
  getExpenseTransactionId: (expense) => {
    return expense?.payment_method === expensePaymentMethodConstants.OPEN_BANKING
      ? expense?.saltedge_id
      : expense?.card_transaction_id;
  },

  /**
   * Returns true when the expense is missing one or more required fields for submission.
   * @param {Object} expense - Expense object to validate.
   * @param {Object} [options={}] - Options object.
   * @param {boolean} [options.isDescriptionMandatory=true] - Whether a missing description counts as missing. False for small-tier companies (H1MVP-5047).
   * @returns {boolean} True if description (when mandatory), expense_type, category_tags, or invoice reference is missing.
   * @example
   * expensesHelpers.hasExpenseMissingRequiredFields({ description: '', expense_type: null }); // true
   */
  hasExpenseMissingRequiredFields: (expense, {isDescriptionMandatory = true} = {}) => {
    if (!expense || typeof expense !== 'object') return false;

    return (isDescriptionMandatory && !expense?.description) || !objectHelpers.isValidValue(expense?.expense_type) || !(expense?.category_tags || []).length;
  },

  /**
   * Returns true when the current user is allowed to submit the expense for review.
   * @param {Object} params - Parameters object.
   * @param {Object} params.expense - Expense object with reported_by and review_status.
   * @param {boolean} params.isAdmin - Whether the current user is an admin.
   * @param {string} params.employeeEmail - Email of the currently authenticated employee.
   * @returns {boolean} True if the user can submit this expense.
   * @example
   * expensesHelpers.isExpenseSubmitAvailable({ expense, isAdmin: false, employeeEmail: 'user@co.com' });
   */
  isExpenseSubmitAvailable: ({expense, isAdmin, employeeEmail}) => {
    if (!expensesHelpers.isExpenseActionsAvailable(expense)) return false;
    const isReporter = expense?.reported_by?.email === employeeEmail;
    const isDraftExpense = expensesHelpers.isExpenseDraft(expense);
    return (isAdmin || isReporter) && isDraftExpense;
  },

  /**
   * Returns true when the reporter is allowed to edit (reopen and re-file) a rejected expense.
   * @param {Object} params - Parameters object.
   * @param {Object} params.expense - Expense object with reported_by and review_status.
   * @param {string} params.employeeEmail - Email of the currently authenticated employee.
   * @returns {boolean} True if the current user reported this rejected expense.
   * @example
   * expensesHelpers.isExpenseEditAvailable({ expense, employeeEmail: 'user@co.com' });
   */
  isExpenseEditAvailable: ({expense, employeeEmail}) => {
    if (!expensesHelpers.isExpenseActionsAvailable(expense)) return false;
    const isReporter = expense?.reported_by?.email === employeeEmail;
    const isRejected = expense?.review_status === expenseReviewStatusConstants.REJECTED;
    return isReporter && isRejected;
  },

  /**
   * Returns an object with start and end date strings derived from the expense's date fields.
   * @param {Object} expense - Expense object with expense_date or created_at.
   * @param {string} [format='YYYY-MM-DD'] - Date format string.
   * @returns {{start: string|null, end: string|null}|null} Date range object, or null when no date data exists.
   * @example
   * expensesHelpers.getExpenseDates({ expense_date: '2024-01-01#2024-01-15' }); // { start: '2024-01-01', end: '2024-01-15' }
   * @example
   * expensesHelpers.getExpenseDates({ expense_date: '2024-01-01' }, 'MM-YYYY'); // { start: '01-2024', end: null }
   */
  getExpenseDates: (expense, format = 'YYYY-MM-DD') => {
    if (!expense?.expense_date) {
      if (expense?.created_at) {
        const createdAt = dateHelpers.getDateFromISO(expense.created_at, format);
        return { start: createdAt, end: createdAt };
      }
      return null;
    }

    let startRaw = expense.expense_date;
    let endRaw = expense?.expense_end_date;

    // support legacy range format "YYYY-MM-DD#YYYY-MM-DD"
    if (typeof startRaw === 'string' && startRaw.includes('#')) {
      [startRaw, endRaw] = startRaw.split('#').map(s => s.trim());
    }

    return {
      start: startRaw ? dateHelpers.getDate(startRaw, format) : null,
      end: endRaw ? dateHelpers.getDate(endRaw, format) : null,
    }
  },

  /**
   * Adds a suggestion string to the front of the persisted expense suggestions list (max 5 items).
   * @param {string} suggestion - Suggestion text to store.
   * @returns {void}
   * @example
   * expensesHelpers.addExpenseSuggestion('Team lunch');
   */
  addExpenseSuggestion: (suggestion) => {
    if (!suggestion) return;

    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    const list = Array.isArray(stored) ? stored : [];

    if (list.includes(suggestion)) return;

    const next = [suggestion, ...list.filter(item => item !== suggestion)].slice(0, 5);
    ls.set(storageKey, next);
  },

  /**
   * Retrieves the persisted list of expense suggestions from local storage.
   * @returns {string[]} Array of suggestion strings, or empty array when none are stored.
   * @example
   * const suggestions = expensesHelpers.getExpenseSuggestions(); // ['Team lunch', 'Office supplies']
   */
  getExpenseSuggestions: () => {
    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    return Array.isArray(stored) ? stored : [];
  },

  /**
   * Removes a specific suggestion string from the persisted expense suggestions list.
   * @param {string} suggestion - Suggestion text to remove.
   * @returns {void}
   * @example
   * expensesHelpers.removeExpenseSuggestion('Team lunch');
   */
  removeExpenseSuggestion: (suggestion) => {
    if (!suggestion) return;

    const storageKey = localStorageKeysConstants.EXPENSE_SUGGESTIONS;
    const stored = ls.get(storageKey);
    const list = Array.isArray(stored) ? stored : [];

    const next = list.filter(item => item !== suggestion);
    ls.set(storageKey, next);
  },
   /**
   * Transforms a transaction query object into an expense query format by mapping specific keys.
   * @param {Object} query - Original query object with transaction-style keys (e.g. amount_lte, date_gte).
   * @returns {Object} New query object with expense-style keys (e.g. low_value, start_date).
   * @example
   * const expenseQuery = expensesHelpers.formatTransactionQueryToExpenseQuery({ amount_lte: 100, date_gte: '2024-01-01' });
   * // Returns: { low_value: 100, start_date: '2024-01-01' }
   */
  formatTransactionQueryToExpenseQuery: (query) => {
    if (!query || typeof query !== 'object') return {};
    const formatQuery = {
      amount_lte: 'low_value',
      amount_gte: 'high_value',
      date_gte: 'start_date',
      date_lte: 'end_date',
    }

    return Object.entries(query).reduce((acc, [key, value]) => {
      const newKey = formatQuery[key] || key;
      return {...acc, [newKey]: value};
    }, {});
  },
  /**
   * Returns the sort order string for expense queries, switching to price-based ordering when an amount filter is active.
   * @param {Object|null} query - Expense query object with optional high_value or low_value filters.
   * @returns {string} Sort order string: '-price' for high_value, 'price' for low_value, '-date' otherwise.
   * @example
   * expensesHelpers.getExpenseOrdering({ high_value: 500 }); // '-price'
   * expensesHelpers.getExpenseOrdering({ low_value: 100 }); // 'price'
   * expensesHelpers.getExpenseOrdering({ search: 'lunch' }); // '-date'
   */
  getExpenseOrdering: (query) => {
    if (query?.high_value != null) return '-price';
    if (query?.low_value != null) return 'price';
    return '-date';
  },
  /**
   * Returns true when the expense is eligible for the Reclaim action (REJECTED status + CARD or WIRE payment).
   * @param {Object} expense - Expense object with review_status and payment_method.
   * @returns {boolean}
   */
  canReclaimExpense: (expense) =>
    expensesHelpers.isExpenseActionsAvailable(expense) &&
    (expense?.payment_method === expensePaymentMethodConstants.CARD ||
      expense?.payment_method === expensePaymentMethodConstants.WIRE) &&
    [expenseReviewStatusConstants.REJECTED, expenseReviewStatusConstants.PARTIALLY_APPROVED].includes(expense?.review_status),

  /**
   * Returns true when the expense is eligible for the Reimburse action (APPROVED or PARTIALLY_APPROVED status + PAID_BY_EMPLOYEE payment).
   * @param {Object} expense - Expense object with review_status and payment_method.
   * @returns {boolean}
   */
  canReimburseExpense: (expense) =>
    expensesHelpers.isExpenseActionsAvailable(expense) &&
    expense?.payment_method === expensePaymentMethodConstants.PAID_BY_EMPLOYEE &&
    [expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.PARTIALLY_APPROVED].includes(expense?.review_status),

  /**
   * Returns true when the expense's expense_type is PROCESSING.
   * @param {Object} expense - Expense object with an expense_type property.
   * @returns {boolean} True if the expense is currently being processed.
   * @example
   * expensesHelpers.isExpenseProcessing({ expense_type: 3 }); // true
   */
  isExpenseProcessing: (expense) => expense?.expense_type === expenseTypesConstants.PROCESSING,
  /**
   * Returns true when expense actions (approve, reject, submit, etc.) are available for the given expense.
   * Actions are unavailable when the payment method is Open Banking.
   * @param {Object} expense - Expense object with a payment_method property.
   * @returns {boolean} True if expense actions can be performed.
   * @example
   * expensesHelpers.isExpenseActionsAvailable({ payment_method: 4 }); // false (open banking)
   * expensesHelpers.isExpenseActionsAvailable({ payment_method: 1 }); // true (card)
   */
  isExpenseActionsAvailable: (expense) => expense?.payment_method !== expensePaymentMethodConstants.OPEN_BANKING,

  /**
   * Converts a month/year + date_type selection into the corresponding API date range filter keys.
   * Returns first and last day of the given month in YYYY-MM-DD format.
   * @param {Object} params - Parameters object.
   * @param {number} params.month - Month number (1–12).
   * @param {number} params.year - Full year (e.g. 2024).
   * @param {string} params.date_field - Date type: 'creation_date' or 'created_at' → {start_date, end_date}; 'expense_date' → {expense_date_start, expense_date_end}.
   * @returns {Object|null} Date range filter object, or null when month or year are missing.
   * @example
   * expensesHelpers.getExpenseListFilterValues({ month: 5, year: 2024, date_field: 'creation_date' });
   * // { start_date: '2024-05-01', end_date: '2024-05-31' }
   * expensesHelpers.getExpenseListFilterValues({ month: 5, year: 2024, date_field: 'expense_date' });
   * // { expense_date_start: '2024-05-01', expense_date_end: '2024-05-31' }
   */
  getExpenseListFilterValues: ({month, year, date_field} = {}) => {
    if (month == null || year == null) return null;

    const pad = (n) => String(n).padStart(2, '0');
    const firstDay = `${year}-${pad(month)}-01`;
    const lastDay = `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;

    if (date_field === 'creation_date' || date_field === 'created_at') {
      return {start_date: firstDay, end_date: lastDay};
    }
    if (date_field === 'expense_date') {
      return {expense_date_start: firstDay, expense_date_end: lastDay};
    }
    return null;
  },
  /**
   * Returns true if the given tag is a mileage expense category tag.
   * @param {{tag_name: string}} tag - Expense tag object.
   * @returns {boolean}
   * @example
   * expensesHelpers.isMileageTag({ tag_name: 'Mileage', tag_family: 'expense' }); // true
   * expensesHelpers.isMileageTag({ tag_name: 'Food', tag_family: 'expense' });    // false
   */
  isMileageTag: (tag) => tag?.tag_name?.toLowerCase() === expenseCategoryTagConstants.MILEAGE,
  /**
   * Returns true when the expense's primary category tag is a mileage tag.
   * @param {Object} expense - Expense object with a category_tags array.
   * @returns {boolean} True if the first category tag is a mileage tag.
   * @example
   * expensesHelpers.isExpenseMileageCategory({ category_tags: [{ tag_name: 'Mileage' }] }); // true
   * expensesHelpers.isExpenseMileageCategory({ category_tags: [{ tag_name: 'Food' }] });    // false
   */
  isExpenseMileageCategory: (expense) => expensesHelpers.isMileageTag({ tag_name: expense?.category_tags?.[0] }),

  /**
   * Displays a dismissible toast notifying the user that a duplicate was dismissed, with an undo action.
   * @param {Object} [params={}] - Parameters object.
   * @param {Function} [params.translation] - i18n translation function used for alert text.
   * @param {Function} [params.onClick] - Callback invoked when the user clicks the undo button.
   * @returns {void}
   * @example
   * expensesHelpers.showDismissUndoAlert({ translation: t, onClick: () => reactivateDuplicates() });
   */
  showDismissUndoAlert: ({translation, onClick, message} = {}) => {
    toast.custom(
      (toastInstance) => {
        const onClose = () => toast.dismiss(toastInstance.id);
        const onUndo = (e) => {
          onClick?.(e);
          onClose();
        };
        return (
          <Space direction='vertical' style={{width: 400}}>
            <Alert
              action={onClick ? (
                <PrimaryTextButton className='underline' size='small' type='link' onClick={onUndo}>
                  {translation?.('undo')}
                </PrimaryTextButton>
              ) : undefined}
              closable
              message={message ?? translation?.('dismissDuplicateSuccess')}
              onClose={onClose}
              showIcon
              type='success'
            />
          </Space>
        );
      },
      {duration: 5000}
    );
  },
}

